import React from 'react';

import { Flex, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { ToolGroup, ToolGroupKind, ToolKind } from 'shared/interfaces/tools/Tool';
import { getToolConfig } from 'shared/interfaces/tools/utils';
import { formatSecondsToFriendlyDuration } from 'shared/utils/format';

interface ToolGroupDetailsProps {
  toolGroup: ToolGroup;
}

export function ToolGroupDetails({ toolGroup }: ToolGroupDetailsProps) {
  const { t } = useTranslation();

  const renderDetailsForToolGroup = () => {
    switch (toolGroup.kind) {
      case ToolGroupKind.CUSTOM_API_REQUEST: {
        const config = getToolConfig(toolGroup.toolConfigs, ToolKind.CUSTOM_API_REQUEST);

        if (!config) return null;

        // Extract domain from URL
        const domain = (() => {
          try {
            const url = new URL(config.requestConfiguration.urlTemplate);
            return url.hostname;
          } catch {
            return config.requestConfiguration.urlTemplate;
          }
        })();

        return (
          <Text size="xs" c="dimmed" lineClamp={1}>
            {`${config.requestConfiguration.method} ${domain}`} - {config.toolName}
          </Text>
        );
      }

      case ToolGroupKind.CALL_TRANSFER: {
        const config = getToolConfig(toolGroup.toolConfigs, ToolKind.CALL_TRANSFER);

        if (!config) return null;

        const transferCount = config.callTransferConfigs?.length || 0;
        return (
          <Text size="xs" c="dimmed">
            {t('SMART_ASSISTANT.TOOLS_SECTION.DETAILS.TRANSFER_RULES', { count: transferCount })}
          </Text>
        );
      }

      case ToolGroupKind.LEAD_COLLECTION: {
        const config = getToolConfig(toolGroup.toolConfigs, ToolKind.COLLECT_LEAD_INFO);

        if (!config) return null;

        const infoCount = config.infoTypesToCollect?.length || 0;
        return (
          <Text size="xs" c="dimmed">
            {t('SMART_ASSISTANT.TOOLS_SECTION.DETAILS.COLLECTING_INFO_TYPES', { count: infoCount })}
          </Text>
        );
      }

      case ToolGroupKind.TAKEOVER_REQUEST: {
        const config = getToolConfig(toolGroup.toolConfigs, ToolKind.TAKEOVER_REQUEST);

        if (!config) return null;

        return (
          <Text size="xs" c="dimmed" lineClamp={1}>
            {config.email}
          </Text>
        );
      }

      case ToolGroupKind.CALENDLY: {
        const config = getToolConfig(toolGroup.toolConfigs, ToolKind.CALENDLY);

        if (!config) return null;

        return (
          <Text size="xs" c="dimmed" lineClamp={1}>
            {config.eventUrl}
          </Text>
        );
      }

      case ToolGroupKind.EMAIL_HANDOFF: {
        const config = getToolConfig(toolGroup.toolConfigs, ToolKind.EMAIL_HANDOFF);

        if (!config) return null;

        return (
          <Text size="xs" c="dimmed" lineClamp={1}>
            {config.emailToHandoff}
          </Text>
        );
      }

      case ToolGroupKind.FOLLOW_UP: {
        const config = getToolConfig(toolGroup.toolConfigs, ToolKind.FOLLOW_UP);
        if (!config) return null;

        const readableDuration = formatSecondsToFriendlyDuration(config.intervalMs / 1000);
        return (
          <Text size="xs" c="dimmed">
            {t('SMART_ASSISTANT.TOOLS_SECTION.DETAILS.EVERY_DURATION', { duration: readableDuration })}
          </Text>
        );
      }

      case ToolGroupKind.CALDOTCOM_APPOINTMENT_SCHEDULING: {
        const config = getToolConfig(toolGroup.toolConfigs, ToolKind.CALDOTCOM_SCHEDULE_MEETING);
        if (!config) return null;

        return (
          <Text size="xs" c="dimmed" lineClamp={1}>
            {t('SMART_ASSISTANT.TOOLS_SECTION.DETAILS.EVENT_ID', { eventId: config.eventTypeId })}
          </Text>
        );
      }

      default:
        return null;
    }
  };

  return (
    <Flex direction="column" justify="center" mih={40}>
      {renderDetailsForToolGroup()}
    </Flex>
  );
}
